import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Lightbulb, CheckCircle, AlertTriangle, Thermometer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SleepEnvironmentGuide() {
  const navigate = useNavigate();

  const auditChecklist = [
    { category: "Light", items: ["Blackout curtains or blinds?", "No LED lights from electronics?", "Night light in bathroom instead of overhead?", "Sunrise alarm clock instead of phone?"] },
    { category: "Temperature", items: ["Room at 65-68°F?", "Breathable bedding materials?", "Fan available if needed?", "Layers you can add/remove?"] },
    { category: "Sound", items: ["Street noise blocked?", "White noise machine or fan?", "Partner's snoring addressed?", "Early morning disruptions?"] },
    { category: "Comfort", items: ["Mattress supportive?", "Pillows right firmness?", "Sheets clean and comfortable?", "Clutter-free space?"] }
  ];

  const improvementIdeas = [
    { issue: "Too much light", fix: "Blackout curtains ($20-50), sleep mask ($10), cover LED lights with tape" },
    { issue: "Wrong temperature", fix: "Adjust thermostat, get a fan, use breathable cotton sheets" },
    { issue: "Noisy environment", fix: "White noise machine ($20), earplugs, heavy curtains for sound dampening" },
    { issue: "Uncomfortable bed", fix: "Memory foam topper ($100-200), new pillows, fresh sheets" },
    { issue: "Electronics in room", fix: "Charge phone outside bedroom, remove TV, use old-fashioned alarm clock" },
    { issue: "Mental clutter", fix: "Keep bedroom for sleep only, journal before bed, tidy space" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
            Week 5 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Sleep Environment Audit
          </h1>
          <p className="text-xl text-secondary-600">Create Your Perfect Sleep Sanctuary</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Moon className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Mission</h3>
              <p className="text-indigo-100 text-lg">
                Walk through your bedroom with fresh eyes. Identify 3 improvements you can make today. 
                Your sleep environment dramatically affects sleep quality—small changes can make a big difference.
              </p>
            </div>
          </div>
        </section>

        {/* Audit Checklist */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Bedroom Audit Checklist</h2>
          <div className="space-y-6">
            {auditChecklist.map((section, index) => (
              <div key={index} className="bg-indigo-50 rounded-xl p-5">
                <h3 className="text-lg font-bold text-indigo-700 mb-3">{section.category}</h3>
                <div className="grid md:grid-cols-2 gap-2">
                  {section.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white p-2 rounded-lg">
                      <div className="w-5 h-5 border-2 border-indigo-300 rounded"></div>
                      <span className="text-sm text-secondary-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Fixes */}
        <section className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 border-2 border-indigo-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-indigo-500" />
            Quick Fixes
          </h2>
          <div className="space-y-3">
            {improvementIdeas.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-bold text-secondary-900">{item.issue}:</span>
                    <p className="text-secondary-600">{item.fix}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Ideal Temperature */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Thermometer className="w-6 h-6 text-indigo-500" />
            The Perfect Sleep Temperature
          </h2>
          <p className="text-secondary-700 mb-4">
            Your body temperature naturally drops at night to initiate sleep. A cool room (65-68°F / 18-20°C) 
            supports this process and helps you fall asleep faster and stay asleep longer.
          </p>
          <div className="bg-indigo-50 rounded-xl p-4">
            <p className="text-secondary-800">
              <strong>Tonight's experiment:</strong> Set your thermostat to 67°F (or use a fan) and notice 
              if you fall asleep faster and wake up feeling more refreshed.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SleepEnvironmentGuide;
